import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail, Globe2 } from "lucide-react";

const CONTENT = {
  fr: {
    lang_switch: "English",
    title: "Politique de confidentialité",
    subtitle: "Un Avocat en Or — Robin Oschmann · BCE / TVA BE 0838.152.848",
    updated: "Dernière mise à jour : 3 juillet 2026",
    intro:
      "Nous prenons votre vie privée au sérieux. Ce document explique, en langage clair, quelles données nous collectons, pourquoi, combien de temps nous les conservons, et comment vous pouvez exercer vos droits. Un Avocat en Or est édité par une entreprise individuelle belge et opère 100 % en Europe, en conformité avec le RGPD (règlement UE 2016/679).",
    sections: [
      {
        h: "1. Qui est le responsable de traitement ?",
        p: [
          "Robin Oschmann, entrepreneur individuel, immatriculé à la Banque-Carrefour des Entreprises sous le n° 0838.152.848 (siège : 54 rue Fond du Sart, 4171 Comblain-au-Pont, Belgique).",
          "Contact vie privée : contact@unavocatenor.com",
        ],
      },
      {
        h: "2. Quelles données collectons-nous ?",
        p: [
          "Compte : adresse email, mot de passe (haché bcrypt), nom, pays de résidence, langue préférée. Optionnellement : identifiant Google si vous vous connectez via Google.",
          "Contenu que vous nous confiez : questions juridiques, photos de documents, enregistrements audio, dossier médical (allergies, traitements, contacts d'urgence), messages du réseau Refuge.",
          "Métadonnées techniques : date/heure, adresse IP (utilisée uniquement pour détecter la juridiction et pour la sécurité), type d'appareil, navigateur.",
          "Localisation : uniquement au moment où vous déclenchez une touche SOS et l'autorisez explicitement. Pour le réseau Refuge, seule la VILLE est stockée — jamais les coordonnées GPS.",
          "Paiement : géré directement par Stripe. Nous ne stockons jamais votre numéro de carte.",
        ],
      },
      {
        h: "3. Pourquoi les traitons-nous ?",
        p: [
          "Fournir le service : répondre à vos questions juridiques, générer les courriers, activer les QR codes médicaux d'urgence.",
          "Sécurité : détecter les abus, prévenir la fraude, protéger le réseau Refuge (auto-suspension après 3 signalements).",
          "Intelligence collaborative anonyme : agréger les retours sur les bureaux administratifs pour aider les prochains utilisateurs (aucune donnée personnelle exposée).",
          "Facturation : gérer les abonnements Individuel, Famille et B2B.",
          "Communication : vous envoyer les emails de confirmation, réinitialisation de mot de passe, ou notifications de sécurité.",
        ],
      },
      {
        h: "4. Combien de temps conservons-nous vos données ?",
        p: [
          "Compte : tant que votre compte existe. Suppression sur demande sous 30 jours maximum.",
          "Enregistrements audio préventifs (Bouclier · touche 3) : auto-effacés après 24 heures via TTL MongoDB — nous n'y avons pas accès manuel.",
          "Alertes SOS analysées : conservées 12 mois maximum, puis anonymisées.",
          "Dossier médical : tant que vous le maintenez actif. Supprimable à tout moment depuis votre profil.",
          "Messages Refuge : conservés 6 mois après la clôture du fil, puis anonymisés.",
          "Journaux techniques (logs) : 90 jours pour raisons de sécurité.",
        ],
      },
      {
        h: "5. Avec qui partageons-nous ces données ?",
        p: [
          "Personne, sauf strictement nécessaire pour délivrer le service, à savoir :",
          "• Fournisseurs d'IA (Anthropic Claude, OpenAI Whisper) via Emergent LLM Key — traitements chiffrés, sans stockage utilisateur final chez le fournisseur.",
          "• Stripe pour les paiements (aucune donnée non nécessaire au paiement transmise).",
          "• Hébergement Kubernetes Emergent, en région Union européenne.",
          "Nous ne vendons AUCUNE donnée. Aucun traqueur publicitaire (pas de Google Analytics, pas de Facebook Pixel). Aucun partage avec des courtiers de données.",
        ],
      },
      {
        h: "6. Sécurité",
        p: [
          "Connexions HTTPS obligatoires (TLS 1.3). Mots de passe hachés avec bcrypt (12 tours). JWT stocké en cookie httpOnly Secure SameSite. Sauvegardes chiffrées.",
          "Pour le réseau Refuge : anonymat double couche. Votre user_id réel n'est JAMAIS exposé aux autres utilisateurs — nous générons un pseudonyme aléatoire (host_anon_id / victim_anon_id).",
        ],
      },
      {
        h: "7. Vos droits (RGPD)",
        p: [
          "Vous avez le droit de :",
          "• Accéder à vos données (export en 1 clic depuis votre profil, à venir — sinon sur demande).",
          "• Rectifier vos données inexactes.",
          "• Effacer votre compte et toutes vos données associées.",
          "• Vous opposer à un traitement, ou le limiter.",
          "• Recevoir vos données dans un format portable.",
          "Envoyez un email à contact@unavocatenor.com — réponse sous 30 jours.",
          "Vous pouvez aussi porter plainte auprès de l'Autorité de protection des données belge (autoriteprotectiondonnees.be) ou de l'autorité de votre pays de résidence.",
        ],
      },
      {
        h: "8. Cookies",
        p: [
          "Nous utilisons uniquement des cookies strictement nécessaires : cookie de session (JWT), cookie de langue, cookie d'acceptation du bandeau. Aucun cookie publicitaire, aucun cookie tiers de tracking.",
        ],
      },
      {
        h: "9. Enfants",
        p: [
          "Notre service n'est pas destiné aux moins de 13 ans (16 ans dans certains pays UE). Si vous êtes parent et pensez que votre enfant nous a fourni des données, contactez-nous — nous supprimerons immédiatement.",
        ],
      },
      {
        h: "10. Modifications",
        p: [
          "Toute modification substantielle de cette politique fera l'objet d'une notification par email et d'un délai raisonnable avant application. La date de dernière mise à jour figure en haut de ce document.",
        ],
      },
      {
        h: "11. Contact",
        p: [
          "Une question, un doute, une plainte ? Écrivez-nous : contact@unavocatenor.com. Nous répondons personnellement, en français, anglais, allemand ou néerlandais.",
        ],
      },
    ],
  },
  en: {
    lang_switch: "Français",
    title: "Privacy Policy",
    subtitle: "A Lawyer in Gold — Robin Oschmann · VAT BE 0838.152.848",
    updated: "Last updated: 3 July 2026",
    intro:
      "We take your privacy seriously. This document explains, in plain language, what data we collect, why, how long we keep it, and how you can exercise your rights. A Lawyer in Gold is operated by a Belgian sole proprietorship and runs 100% in Europe, in compliance with the GDPR (EU regulation 2016/679).",
    sections: [
      {
        h: "1. Who is the data controller?",
        p: [
          "Robin Oschmann, sole proprietor, registered with the Belgian Crossroads Bank for Enterprises under n° 0838.152.848 (registered office: 54 rue Fond du Sart, 4171 Comblain-au-Pont, Belgium).",
          "Privacy contact: contact@unavocatenor.com",
        ],
      },
      {
        h: "2. What data do we collect?",
        p: [
          "Account: email address, password (bcrypt-hashed), name, country of residence, preferred language. Optionally: Google identifier if you sign in via Google.",
          "Content you entrust to us: legal questions, document photos, audio recordings, medical record (allergies, treatments, emergency contacts), Refuge network messages.",
          "Technical metadata: date/time, IP address (used only for jurisdiction detection and security), device type, browser.",
          "Location: only when you trigger an SOS button and explicitly authorize it. For the Refuge network, only the CITY is stored — never precise GPS coordinates.",
          "Payment: handled directly by Stripe. We never store your card number.",
        ],
      },
      {
        h: "3. Why do we process this data?",
        p: [
          "Service delivery: answering your legal questions, generating letters, enabling medical emergency QR codes.",
          "Security: detecting abuse, preventing fraud, protecting the Refuge network (auto-suspension after 3 reports).",
          "Anonymous collaborative intelligence: aggregating feedback on administrative offices to help future users (no personal data exposed).",
          "Billing: managing Individual, Family and B2B subscriptions.",
          "Communication: sending confirmation emails, password resets, or security notifications.",
        ],
      },
      {
        h: "4. How long do we keep your data?",
        p: [
          "Account: as long as your account exists. Deletion on request within 30 days maximum.",
          "Preventive audio recordings (Shield · button 3): auto-deleted after 24 hours via MongoDB TTL — we have no manual access.",
          "Analyzed SOS alerts: kept 12 months maximum, then anonymized.",
          "Medical record: as long as you keep it active. Deletable at any time from your profile.",
          "Refuge messages: kept 6 months after thread closure, then anonymized.",
          "Technical logs: 90 days for security reasons.",
        ],
      },
      {
        h: "5. Who do we share this data with?",
        p: [
          "Nobody, except when strictly necessary to deliver the service, namely:",
          "• AI providers (Anthropic Claude, OpenAI Whisper) via Emergent LLM Key — encrypted processing, no end-user storage at provider.",
          "• Stripe for payments (no data beyond what payment requires).",
          "• Emergent Kubernetes hosting, European Union region.",
          "We do NOT sell any data. No advertising trackers (no Google Analytics, no Facebook Pixel). No sharing with data brokers.",
        ],
      },
      {
        h: "6. Security",
        p: [
          "Mandatory HTTPS connections (TLS 1.3). Passwords hashed with bcrypt (12 rounds). JWT stored in httpOnly Secure SameSite cookie. Encrypted backups.",
          "For the Refuge network: double-layer anonymity. Your real user_id is NEVER exposed to other users — we generate a random pseudonym (host_anon_id / victim_anon_id).",
        ],
      },
      {
        h: "7. Your rights (GDPR)",
        p: [
          "You have the right to:",
          "• Access your data (one-click export from your profile, coming soon — otherwise on request).",
          "• Rectify inaccurate data.",
          "• Delete your account and all associated data.",
          "• Object to or restrict processing.",
          "• Receive your data in a portable format.",
          "Send an email to contact@unavocatenor.com — response within 30 days.",
          "You may also file a complaint with the Belgian Data Protection Authority (autoriteprotectiondonnees.be) or the authority of your country of residence.",
        ],
      },
      {
        h: "8. Cookies",
        p: [
          "We use only strictly necessary cookies: session cookie (JWT), language cookie, banner acceptance cookie. No advertising cookies, no third-party tracking cookies.",
        ],
      },
      {
        h: "9. Children",
        p: [
          "Our service is not intended for anyone under 13 (16 in some EU countries). If you are a parent and believe your child provided us data, contact us — we will delete immediately.",
        ],
      },
      {
        h: "10. Changes",
        p: [
          "Any material change to this policy will be notified by email with reasonable notice before it applies. The last-updated date appears at the top of this document.",
        ],
      },
      {
        h: "11. Contact",
        p: [
          "A question, a doubt, a complaint? Write to us: contact@unavocatenor.com. We answer personally, in French, English, German or Dutch.",
        ],
      },
    ],
  },
};

export default function Privacy() {
  const [lang, setLang] = useState("fr");
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="privacy-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition"
            data-testid="privacy-lang-toggle"
          >
            <Globe2 className="h-3.5 w-3.5" />
            {c.lang_switch}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-sm bg-amber-100 text-amber-800 border border-amber-300 flex items-center justify-center">
            <Shield className="h-5 w-5" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif text-slate-900" data-testid="privacy-title">
            {c.title}
          </h1>
        </div>
        <p className="text-sm text-slate-600">{c.subtitle}</p>
        <p className="text-xs text-slate-500 mt-1">{c.updated}</p>

        <p className="mt-6 text-base text-slate-700 leading-relaxed">{c.intro}</p>

        <div className="mt-8 space-y-8">
          {c.sections.map((s, idx) => (
            <section key={idx} data-testid={`privacy-section-${idx}`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">{s.h}</h2>
              <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
                {s.p.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 p-4 rounded-sm bg-amber-50 border border-amber-200 flex items-start gap-3">
          <Mail className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>{lang === "fr" ? "Une question ?" : "A question?"}</strong>{" "}
            <a href="mailto:contact@unavocatenor.com" className="underline hover:no-underline">
              contact@unavocatenor.com
            </a>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-4 text-xs text-slate-500">
          <Link to="/terms" className="hover:text-slate-900 underline">
            {lang === "fr" ? "Conditions d'utilisation" : "Terms of Service"}
          </Link>
          <Link to="/media-kit" className="hover:text-slate-900 underline">
            {lang === "fr" ? "Kit presse" : "Media Kit"}
          </Link>
          <Link to="/" className="hover:text-slate-900 underline">
            unavocatenor.com
          </Link>
        </div>
      </main>
    </div>
  );
}
